import { MarkDown } from '@interfaces/questions';
import { JSONSchema7 } from 'json-schema';
import { WorkflowItemData } from '@api/projects/interfaces';
import { rdiffResult } from 'recursive-diff';

/**
 * Hilfsfunktion um typ-sicher casten zu können.
 *
 * Die "type assertions" von TS prüfen die Objekte nicht merh auf Vollständigkeit:
 *
 * Die beiden Ausdrücke sind z.B. beide gültig.
 * const item: IWorkflowItem = {} as IQuestionnaireWorkflowItem;
 * const item: IWorkflowItem = <IQuestionnaireWorkflowItem>{};
 *
 * Mit der Funktion kann castet werden, aber trotzdem die Typ-Prüfung durchgeführt werden.
 * Der folgende Ausdruck wird korrekt als Fehler gemeldet.
 * const item: IWorkflowItem = cast<IQuestionnaireWorkflowItem>({});
 *
 * @param value Wert
 */
export function cast<T>(value: T): T {
	return value;
}

/**
 * Update trigger.
 *
 * Workflowitems können automatisch aktualisiert werden, wenn sich die Daten von anderen
 * oder dem eigenen Item ändern. Dazu hängt an jedem Trigger eine Liste vonWorkflowitem-Ids,
 * von den der Trigger abhängt. Die sind dann noch unterteilt in "inprogress", "completed" und "aggregate".
 *
 * * inprogress: WorkflowItems in Bearbeitung
 * * completed: Abgeschlossene Workflowitems
 * * aggregate: Zusammenfassungen abgeschlossener Workflowitems
 */
export type TriggerType = 'inprogress' | 'completed' | 'aggregate' | 'completing' | 'update_inprogress';
export type ParticipantTriggerType = 'create_participant' | 'delete_participant' | 'update_participant' | 'prepare_participant' | 'participant';
export interface IWorkflowItemTrigger {
	dependsOn: {
		user?: boolean;
		user_groups?: string[];
		participant?: boolean;
		participants?: boolean;
		inprogress?: string[];
		completed?: string[];
		aggregate?: string[];
	};
	type: TriggerType;
	script: string;
}

export interface IParticipantTrigger {
	dependsOn: {
		user?: boolean;
		user_groups?: string[];
		participant?: boolean;
		participants?: boolean;
		inprogress?: string[];
		completed?: string[];
		aggregate?: string[];
	};
	type: ParticipantTriggerType;
	script: string;
}

export interface ReportViewConfigs {
	itemDetailView?: ReportViewConfig;
	participantDetailView?: ReportViewConfig;
}

/**
 * String wird als boolean Variable gesucht und ausgewertet.
 */
export interface ReportViewConfig {
	isVisible?: boolean;
	isPrintable?: boolean;

	visibleIf?: string;
	printableIf?: string;
}

/**
 * Mittlere Hierarchieebene in der Auswertung.
 * Subinterfaces:
 * ContenBlockReportItem(GSP-631): Freitext-Block
 * ListReportItem: Sammlung von Balkendiagrammen oder Label-Value-Zeilen
 */
export interface IReportItem {

	reportViewConfigs?: ReportViewConfigs;
	type: string;
	title: string;
	// ID des ReportItem zur Kategorisierung
	id: string;
}

export interface IReportPrintConfig {
	forcePageBreak?: boolean;
}
export interface IReportOptions {
	printConfig?: IReportPrintConfig
}
export interface IReportItemCategory {
	label: string;
	// Sammlung von IReportItem.id
	entries: string[];
	options?: IReportOptions;
}

export type StartCondition = {
	type: 'value_equal' | 'value_unequal';
	key: string;
	value: string | number | boolean;
} | {
	type: 'value_truthy' | 'value_falsy';
	key: string;
} | {
	type: 'value_time_since',
	key: string;
	days?: number;
	months?: number;
} | {
	type: 'item_started' | 'item_completed'
	itemId: string;
};

export interface CustomState {
	id: string;
	label: string;
	color: string; // Farbe, mit der der Zustand in der Teilnehmerübersicht erscheint
	colorShouldBeBackground: boolean; //true: color wird Hintergrund mit Schriftfarbe weiß, false: color wird Schriftfarbe mit Hintergrund weiß
	symbol?: string; // zeigt auf resource URL
}

export interface CustomStateTransition {
	label: string; // Wie die transition im Frontend genannt wird
	startStateId: string;
	endStateId: string;
	requiredPermissions?: string[]; // OR verküpfte Permissions
	isBulkActionPossible?: boolean;
	requiresAuthentication?: boolean;
}

export interface IWorkflowItem {

	id: string;
	name: string;
	shortName?: string;
	description: string;
	category: string;
	config?: IWorkflowItemConfig;

	readonly type: string;

	triggers?: IWorkflowItemTrigger[];

	recommendedViewMode?: 'compact' | 'wizard';
	
	/** Die startConditions sind 'und'-Verknüpft */
	startConditions?: StartCondition[];
	customStates?: CustomState[];
	customStateTransitions?: CustomStateTransition[];
	
	hasQueries?: boolean;
}

export interface IWorkflowItemConfig {
	confDialog?: ITextButtonConfig;
	disableConfirmDialog?: boolean
	disableDetailPage?: boolean;
	disableOverview?: boolean;
	disableWizard?: boolean;
	primaryButtonLabel?: string;
}

export interface ITextButtonConfig {
	textMessage?: string;
	textOK?: string;
}

export interface IQuestionnaireWorkflowItem extends IWorkflowItem {

	readonly type: 'Questionnaire';

	groups: IQuestionnaireGroup[];

	showOverview?: boolean;

}

export interface RandomizationWorkflowItem extends IWorkflowItem {
	readonly type: 'Randomization';

	maxParticipants: number; // N
	groups: Array<{
		maximumParticipantsInGroup: number;
		groupValue: string; // Wert, der für Gruppe gespeichert wird
	}>;
	participantValueKey: string;  // key, unter dem der Zuweisungswert in IParticipant.data gespeichert werden soll
	workflowItemValueKey: string; // key, unter dem der Zuweisungswert in WorkflowItemData.data gespeichert wird
}

export interface IQuestionnaireOverviewItem {
	// key in WorkflowItemData.data
	key: string;
	// Label
	label: string;
	// optionale Einheit (db, kg, etc)
	unit?: string;
	// optionales Template. Default wird '{{value}}' bzw. '{{value}} {{unit}}' verwendet
	template?: string;
	// optional, zeige nur Fehlwert
	showOnlyMissingValue?: boolean;
}

export interface IQuestionnaireGroup {

	title: string;

	preface?: MarkDown[],

	epilog?: MarkDown[],

	items: IQuestionnaireItem[];

	groupOverview?: IQuestionnaireOverviewItem[];

	isWizardSkipAllowed?: boolean;
	isProcedure?: boolean;

	missingValueKey?: string;

	showIfDevicePresent?: Messgeraete[];
	showIfDeviceAbsent?: Messgeraete[];

	showIf?: string;

	startText?: string;
}

export interface IQuestionnaireItem {

	readonly type: string;
	// Überschrift zur Frage
	title?: string;
	// Vorwort (z.B. Erläuterungen zur Beantwortung, die vor der eigentlichen Frage angezeigt werden)
	preface?: MarkDown[];
	// Beschreibung (wenn notwendig)
	description?: MarkDown;
	// Wenn true, wird die Beschreibung im ablenkungsfreien Wizard weiter unten angezeigt
	alignBottom?: boolean;
	// true: kann nur als Ganzes neu gestartet werden
	isProcedure?: boolean;
	// true: Fehlwert gesetzt
	missingValueKey?: string;
	// item wird nur gezeigt, wenn key unter showIf im WOrkflowItemData truthy ist
	showIf?: string;
	
	hideCheckmarks?: boolean;
}

export interface IWorkflow {

	_id?: string;
	name: string;
	description: string;

	items: IWorkflowItem[];

}

export interface ICreateParticipantWorkflowItem extends IWorkflowItem {
	type: 'CreateParticipant',
	schema: JSONSchema7,
}

export interface IParticipantDetails {
	// Spaltendefinition für participant-list
	list?: {
		// Text in Kopfzeile
		header: string;
		// Breite der Spalte
		width?: string;
		// Interpolation der Daten. z.B. '{{personal.firstName}} {{personal.lastName}}'
		data: string;
	}[];
	// Infoblock in participant-info
	info?: {
		// Interpolation für Namen
		name: string;
		// zusätzliche Details
		details?: {
			showIf?: string; // Optionale Möglichkeit einen Eintrag abhängig von einem Eintrag in IParticipant.data anzuzeigen
			label?: string; // Optionales Label
			template: string; // Interpolation für Wert
		}[];
		showCreationDate?: boolean; // Default true
	};
	pseudonymKey?: string;
	protocol?: {
		participantIdentifierKey?: string;
	}
	searchableInProtectedViewCombined?: string[];
	searchableInProtectedViewStandalone?: string[];
	usedParticipantList?: 'simple' | 'verlaufsdiagramm';
}
export interface IParticipantConfig {
	_id?: string;
	schema: JSONSchema7;
	details?: IParticipantDetails;
	triggers?: IParticipantTrigger[];
}

export interface IProjectConfig {
	_id?: string;
	schema: JSONSchema7;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface IParticipant<T = any> {
	id?: string;
	creationTimestamp?: number;
	required_group_ids?: string[];
	data: T;
}

export interface IUserGroup<T = unknown> {
	id: string;
	name: string;
	description: string;
	systemProvided?: boolean;
	triggerId?: string;
	roles?: string[];
	permissions?: string[];
	data?: T; // properties
}

export interface JSONSchema7Extensions {
	$txkey?: string;
	$order?: string[];
	$renderhint?: string;
	$initialDate?: string;
}

export enum Messgeraete {
	TouchScreen = 'device.TouchScreen',
	ButtonDevice = 'device.ButtonDevice',
	TappingPen = 'device.TappingPen',
	ExternalAudiometry = 'device.ExternalAudiometry',
	SoundDevice = 'device.SoundDevice',
	CalibratedHeadphones = 'device.CalibratedHeadphones',
	Audiometry_Sh_HDA_300__Sb_X_G1__Th_PS_TPY_2003_KBB = 'device.Audiometry_Sh_HDA_300__Sb_X_G1__Th_PS_TPY_2003_KBB',
	Audiometry_bd_DT_770_PRO_32__Sb_X_G1 = 'device.Audiometry_bd_DT_770_PRO_32__Sb_X_G1',
	TestHardware = 'device.TestHardware',
	ExternalBicycleErgometer = 'device.ExternalBicycleErgometer',
}

export interface ParticipantSearchRequest{
	searchString: string;
}

export type ProtocolType = 'participant-create' | 'participant-update' | 'participant-delete' | 'workflow-item-inprogress' | 'workflow-item-completed' | 'workflow-item-inprogress-reset' | 'workflow-item-completed-reset' | 'custom-state-change' | 'config-archive-upload';

export interface ProtocolCustomStateChange {
	oldLabel: string;
	oldId: string;
	newLabel: string;
	newId: string;
}

// Für Frontend
export interface ProtocolEntryDto<T = IParticipant | WorkflowItemData> {
	id: string;
	timestamp: number;
	username: string;
	identifier?: { itemid?: string, pid: string }; // Für Participant: pid. Für WFData: itemid und pid
	translation?: {item?: string, participant?: string };
	type: ProtocolType;
	data?: T;
	stateChange?: ProtocolCustomStateChange;
	diff?: rdiffResult[];
}